import React from "react"
import { Button, Card, Col, Empty, Input, InputNumber, Space, Table, Tag, Tooltip, Typography } from "antd"
import { FileTextOutlined, InfoCircleOutlined, MinusCircleOutlined } from "@ant-design/icons"
import { TMovementElementItem } from "../../../../models/salesModel"
import { textNumberFormater } from "../../../../utils"

const { Text } = Typography

interface NoteItemsDetailCardProps {
  noteItems: TMovementElementItem[]
  billItemList?: TMovementElementItem[]
  noteType: 'CREDIT_NOTE' | 'DEBIT_NOTE'
  isPhysical: boolean
  isAnnulment: boolean
  itemRemainingQty: Record<number, number>
  onUpdateItemName: (id: number, name: string) => void
  onUpdateItemQuantity: (id: number, quantity: number) => void
  onUpdateItemUnitPrice: (id: number, unit_price: number) => void
  onRemoveItem: (id: number) => void
  children?: React.ReactNode
}

export const NoteItemsDetailCard: React.FC<NoteItemsDetailCardProps> = ({
  noteItems,
  billItemList,
  noteType,
  isPhysical,
  isAnnulment,
  itemRemainingQty,
  onUpdateItemName,
  onUpdateItemQuantity,
  onUpdateItemUnitPrice,
  onRemoveItem,
  children
}) => {
  return (
    <Col xs={24} xl={isAnnulment ? 24 : 16} xxl={isAnnulment ? 24 : 17}>
      <Card 
        title={
          <Space align="center" wrap size={8}>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontWeight: 600 }}>Detalle de la Nota</span>
            {noteItems.length > 0 && (
              <Tag color="blue">{noteItems.length} ítem{noteItems.length === 1 ? '' : 's'}</Tag>
            )}
          </Space>
        } 
        className="card shadow-sm"
        size="small"
      >
        <Table
          size="small"
          dataSource={noteItems}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE} 
                description="No ha añadido ítems a la nota. Seleccione ítems de la factura original arriba o agregue un concepto."
              />
            )
          }}
          columns={[
            { 
              title: "Item / Concepto", 
              dataIndex: "name",
              minWidth: 120,
              render: (text: string, record: any) => record.is_custom_charge ? (
                <Input 
                  value={text} 
                  onChange={(e) => onUpdateItemName(record.id, e.target.value)} 
                  placeholder="Descripción del cargo" 
                  size="small"
                  style={{ minWidth: 120 }}
                />
              ) : (
                <Text strong style={{ fontSize: 13 }}>{text}</Text>
              )
            },
            { 
              title: "Cant.", 
              dataIndex: "quantity", 
              width: 65,
              align: "center" as const,
              render: (text: any, record: any) => {
                if (isAnnulment) {
                  return (
                    <Tag color="blue" style={{ fontWeight: 600, fontSize: 13, padding: '2px 8px' }}>
                      {text}
                    </Tag>
                  )
                }
                const maxQty = (noteType === 'CREDIT_NOTE' && isPhysical) 
                  ? (itemRemainingQty[record.id] ?? record.quantity ?? 1)
                  : (billItemList?.find(i => i.id === record.id)?.quantity ?? undefined)
                return (
                  <InputNumber 
                    min={1} 
                    value={text} 
                    max={maxQty}
                    onChange={(val) => onUpdateItemQuantity(record.id, val || 1)}
                    size="small"
                    style={{ width: '100%', maxWidth: 58 }}
                  />
                )
              }
            },
            ...(noteType === 'CREDIT_NOTE' && isPhysical ? [{
              title: "Precio Unit.",
              dataIndex: "unit_price",
              width: 95,
              align: "right" as const,
              render: (text: any) => textNumberFormater(text)
            }] : []),
            ...(!isPhysical || noteType === 'DEBIT_NOTE' ? [
              {
                title: "Precio Orig.",
                width: 85,
                align: "right" as const,
                render: (_: any, record: any) => {
                  const original = billItemList?.find(i => i.id === record.id)
                  if (!original || record.is_custom_charge) {
                    return <Text type="secondary">-</Text>
                  }
                  return <Text>{textNumberFormater(original.unit_price)}</Text>
                }
              },
              {
                title: (
                  <span>
                    {noteType === 'CREDIT_NOTE' ? "Rebaja / Ajuste" : "Mayor Valor"}
                    <Tooltip title={noteType === 'CREDIT_NOTE' 
                      ? "Monto unitario a descontar de la factura (no puede superar el precio original)" 
                      : "Monto unitario adicional a cobrar en la factura"}
                    >
                      <InfoCircleOutlined style={{ marginLeft: 4, color: '#1890ff' }} />
                    </Tooltip>
                  </span>
                ),
                dataIndex: "unit_price",
                width: 120,
                align: "right" as const,
                render: (text: any, record: any) => {
                  const original = billItemList?.find(i => i.id === record.id)
                  const maxPrice = (noteType === 'CREDIT_NOTE' && original && !record.is_custom_charge) 
                    ? (original.unit_price || 0) 
                    : undefined
                  return (
                    <InputNumber 
                      min={0.01}
                      max={maxPrice}
                      value={text}
                      size="small"
                      style={{ width: '100%', minWidth: 95 }}
                      formatter={(value: any) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
                      onChange={(val: any) => onUpdateItemUnitPrice(record.id, val || 0)}
                    />
                  )
                }
              },
              {
                title: "Precio Resultante",
                width: 100,
                align: "right" as const,
                render: (_: any, record: any) => {
                  const original = billItemList?.find(i => i.id === record.id)
                  if (!original || record.is_custom_charge) {
                    return <Text type="secondary">-</Text>
                  }
                  const origPrice = Number(original.unit_price) || 0
                  const adjustment = Number(record.unit_price) || 0
                  const resultingPrice = noteType === 'CREDIT_NOTE' 
                    ? Math.max(0, origPrice - adjustment) 
                    : (origPrice + adjustment)

                  return (
                    <Tag color={noteType === 'CREDIT_NOTE' ? 'cyan' : 'green'} style={{ fontWeight: 600 }}>
                      {textNumberFormater(resultingPrice)}
                    </Tag>
                  )
                }
              }
            ] : []),
            { 
              title: "Total", 
              dataIndex: "total_amount_net", 
              width: 90,
              align: "right" as const, 
              render: (text: any) => <Text strong>{textNumberFormater(text)}</Text> 
            },
            { 
              title: "", 
              width: 36, 
              align: "center" as const,
              render: (_: any, record: any) => {
                if (isAnnulment) {
                  return null
                }
                return (
                  <Button 
                    type="link" 
                    danger 
                    icon={<MinusCircleOutlined />} 
                    onClick={() => onRemoveItem(record.id)}
                    title="Eliminar"
                    style={{ padding: 0 }}
                  />
                )
              }
            }
          ]}
        />
        {children}
      </Card>
    </Col>
  )
}
